import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Utils
import { openColorEyeDropper, isEyeDropperSupported } from '../../../../../../utils/color/eyedropperUtil';
import { hasNoModifierKeys } from '../../../../../../utils/keyboard/keyboardUtility';

// Actions
import { updateSelectedElementsBackgroundColors, updateSelectedElementsColors } from '../colorActions';

// Types
import { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';
import { isCurrentlyEditing } from '../../../../../../element/selection/currentlyEditingSelector';

/**
 * Hook to handle eye dropper keyboard shortcut (i key) for color tools
 *
 * @param selectedElements - List of selected elements
 * @param supportBackgroundColor - Whether element supports background color (false = foreground color)
 * @param isPopupOpen - Current popup state (undefined = always active)
 * @param shouldApplyBackgroundColor - Whether to apply background color
 */
const useColorToolEyeDropper = (
    selectedElements: Immutable.List<ImMNElement>,
    supportBackgroundColor = false,
    isPopupOpen?: boolean,
    shouldApplyBackgroundColor?: boolean,
) => {
    const dispatch = useDispatch();
    const isCurrentlyEditingElement = useSelector(isCurrentlyEditing);

    useEffect(() => {
        // Only handle when we have exactly one selected element and not currently editing
        if (selectedElements?.size !== 1 || isCurrentlyEditingElement) {
            return;
        }

        const handleKeyDown = async (event: KeyboardEvent) => {
            // Check if the 'i' key is pressed without any modifiers
            if (event.key !== 'i' || !hasNoModifierKeys(event)) {
                return;
            }

            // Prevent default behavior and stop propagation
            event.preventDefault();
            event.stopPropagation();

            // Check if eye dropper is supported
            if (!isEyeDropperSupported()) {
                console.warn('EyeDropper API is not supported in this browser');
                return;
            }

            try {
                const color = await openColorEyeDropper();
                if (!color) {
                    return;
                }

                // Determine whether to apply background or foreground color
                const shouldApplyBackground = isPopupOpen ? shouldApplyBackgroundColor : supportBackgroundColor;

                if (shouldApplyBackground) {
                    dispatch(updateSelectedElementsBackgroundColors(color, null));
                } else {
                    dispatch(
                        updateSelectedElementsColors({
                            color,
                            useExistingColorSpace: false,
                            secondaryColor: null,
                        }),
                    );
                }
            } catch (error) {
                console.error('Failed to use eyeDropper:', error);
            }
        };

        // Add the event listener with capture to ensure we get the event before other listeners
        document.addEventListener('keydown', handleKeyDown, { capture: true });

        // Cleanup function to remove the event listener
        return () => {
            document.removeEventListener('keydown', handleKeyDown, { capture: true });
        };
    }, [selectedElements, supportBackgroundColor, isPopupOpen, shouldApplyBackgroundColor, isCurrentlyEditingElement]);
};

export default useColorToolEyeDropper;
